import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

const KEY = 'veyjex_intro_seen';
const LOGS = [
  'SYSTEM_KERNEL_INIT ............ OK',
  'S.I._BOT_NEURAL_LINK .......... ESTABLISHED',
  'MARKET_FEEDS [TOP-10] ......... SYNCED',
  'RISK_ENGINE_CALIBRATION ....... 100%',
  'QUANT_NODES ................... 10/10 OK',
  'BSC_CHAIN_56 / USDT_BEP20 ..... LINKED',
];
const CANDLES = Array.from({ length: 28 }, (_, i) => ({
  h: 18 + ((i * 37) % 46), up: (i * 7) % 5 !== 0, y: 40 - ((i * 13) % 30) + i,
}));

function BootLog() {
  return (
    <div className="absolute left-6 sm:left-10 bottom-10 font-mono text-[10px] sm:text-xs text-vj-neon/80 space-y-1 text-left">
      {LOGS.map((l, i) => (
        <motion.div key={l} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.12 + i * 0.16, duration: 0.2 }}>
          <span className="text-vj-gold/80">[{(0.12 + i * 0.16).toFixed(2)}s]</span> {l}
        </motion.div>
      ))}
    </div>
  );
}

function CandleField() {
  return (
    <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-end justify-center gap-[6px] sm:gap-2 opacity-40 pointer-events-none">
      {CANDLES.map((c, i) => (
        <motion.div key={i} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }}
          transition={{ delay: 1.1 + i * 0.03, duration: 0.35 }} style={{ height: c.h * 2, marginBottom: c.y * 2, originY: 1 }}
          className={`w-1.5 sm:w-2.5 rounded-sm ${c.up ? 'bg-vj-neon/70' : 'bg-vj-gold/70'}`} />
      ))}
    </div>
  );
}

function Core() {
  return (
    <div className="relative flex flex-col items-center">
      {[0, 1, 2].map((i) => (
        <motion.div key={i} className="absolute top-1/2 left-1/2 rounded-full border border-vj-neon/40"
          style={{ width: 220, height: 220, marginLeft: -110, marginTop: -150 }}
          initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: [0.2, 1, 2.4], opacity: [0, 0.8, 0] }}
          transition={{ delay: 0.3 + i * 0.5, duration: 1.8, ease: 'easeOut' }} />
      ))}
      <motion.img src="/veyjex-emblem.webp" alt="" className="relative w-56 sm:w-72 mix-blend-screen vj-emblem-mask drop-shadow-[0_0_40px_rgba(57,255,20,0.45)]"
        initial={{ scale: 0.6, opacity: 0, filter: 'blur(12px)' }}
        animate={{ scale: [0.6, 1.08, 1], opacity: [0, 1, 1], filter: ['blur(12px)', 'blur(0px)', 'blur(0px)'], x: [0, -4, 3, 0] }}
        transition={{ delay: 1.2, duration: 1.4, ease: [0.16, 1, 0.3, 1] }} />
      <motion.img src="/veyjex-wordmark.webp" alt="VEYJEX" className="relative w-72 sm:w-[30rem] -mt-2 mix-blend-screen vj-wordmark-mask"
        initial={{ opacity: 0, y: 14, clipPath: 'inset(0 100% 0 0)' }} animate={{ opacity: 1, y: 0, clipPath: 'inset(0 0% 0 0)' }}
        transition={{ delay: 2.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
      <motion.div className="mt-5 px-6 font-mono text-[9px] sm:text-xs tracking-[0.18em] sm:tracking-[0.25em] text-vj-neon flex items-center justify-center gap-2 text-center"
        initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0.6, 1] }} transition={{ delay: 3.3, duration: 0.6 }}>
        <span className="w-2 h-2 shrink-0 rounded-full bg-vj-neon animate-pulse" /> S.I. BOT CORE ONLINE · TOP-10 MARKETS LINKED · USDT BEP20 READY
      </motion.div>
    </div>
  );
}

export function IntroSequence() {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(() => typeof window !== 'undefined' && !sessionStorage.getItem(KEY));

  const close = () => { sessionStorage.setItem(KEY, '1'); setShow(false); };

  useEffect(() => {
    if (!show) return undefined;
    const tm = setTimeout(close, reduce ? 900 : 4600);
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(tm); window.removeEventListener('keydown', onKey); };
  }, [show, reduce]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div key="intro" data-testid="veyjex-intro-overlay" className="fixed inset-0 z-[100] bg-vj-obsidian overflow-hidden grid place-items-center text-center"
          exit={{ opacity: 0, scale: 1.12 }} transition={{ duration: 0.7, ease: 'easeInOut' }}>
          <div className="absolute inset-0 vj-grid-bg opacity-50" />
          <div className="absolute inset-0 vj-scanlines pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(57,255,20,0.12),transparent_55%)]" />
          {!reduce && <motion.div className="absolute inset-x-0 h-24 bg-gradient-to-b from-transparent via-vj-neon/15 to-transparent"
            initial={{ top: '-10%' }} animate={{ top: '110%' }} transition={{ duration: 1.3, ease: 'linear' }} />}
          {!reduce && <CandleField />}
          {!reduce && <BootLog />}
          {reduce ? <img src="/veyjex-logo-512.webp" alt="VEYJEX" className="w-64 mix-blend-screen" /> : <Core />}
          <motion.div className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-vj-gold via-vj-neon to-vj-emerald"
            initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: reduce ? 0.9 : 4.6, ease: 'linear' }} />
          <button onClick={close} data-testid="veyjex-intro-skip-btn"
            className="absolute top-6 right-6 sm:right-8 z-10 px-4 py-2 font-mono text-xs text-vj-neon bg-vj-charcoal/90 border border-vj-neon/30 rounded hover:bg-vj-neon/10 transition-colors uppercase tracking-widest">
            Skip intro [Esc]
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
